import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Eye, RefreshCw, Send } from "lucide-react";
import moment from "moment";
import axiosInstance from "../../../services/axiosInstance";
import apiEndPointsConfig from "../../../services/apiEndPoints";

// Log of the daily HRMS push. Each business day is sent the next day at noon;
// this screen shows, per day, whether that happened -- and makes a day that
// never went out impossible to miss.

const URL = () => apiEndPointsConfig.hrmsSync;

const STATE_META = {
  synced: { label: "Synced", bg: "#ecfdf5", fg: "#059669" },
  partial: { label: "Partial", bg: "#fffbeb", fg: "#d97706" },
  failed: { label: "Failed", bg: "#fef2f2", fg: "#dc2626" },
  not_synced: { label: "Not synced", bg: "#fef2f2", fg: "#dc2626" },
  running: { label: "Running", bg: "#eff6ff", fg: "#2563eb" },
  scheduled: { label: "Scheduled", bg: "#f3f4f6", fg: "#6b7280" },
};

const ROW_META = {
  sent: { label: "Sent", bg: "#ecfdf5", fg: "#059669" },
  failed: { label: "Failed", bg: "#fef2f2", fg: "#dc2626" },
  skipped: { label: "Skipped", bg: "#f3f4f6", fg: "#6b7280" },
};

const TRIGGER_LABEL = { cron: "Daily cron", catchup: "Catch-up", manual: "Manual" };

const Pill = ({ meta }) => (
  <Chip label={meta.label} size="small" sx={{ bgcolor: meta.bg, color: meta.fg, fontWeight: 700 }} />
);

export default function HrmsSync() {
  const [days, setDays] = useState([]);
  const [range, setRange] = useState(14);
  const [syncTime, setSyncTime] = useState("12:00");
  const [startDate, setStartDate] = useState(null); // first day the sync covers
  const [notSynced, setNotSynced] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState({ text: "", severity: "success" });
  const [running, setRunning] = useState(""); // date being sent
  const [pickDate, setPickDate] = useState(moment().subtract(1, "day").format("YYYY-MM-DD"));

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(`${URL()}/days`, { params: { days: range } });
      setDays(data?.data || []);
      setSyncTime(data?.syncTime || "12:00");
      setStartDate(data?.startDate || null);
      setNotSynced(data?.notSyncedCount || 0);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.message || "Could not load the HRMS sync log");
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const runFor = async (date) => {
    setRunning(date);
    try {
      const { data } = await axiosInstance.post(`${URL()}/run`, { date });
      setNotice({ text: data?.message || "Sent", severity: data?.data?.status === "partial" ? "warning" : "success" });
    } catch (err) {
      setNotice({ text: err?.response?.data?.message || "HRMS sync failed", severity: "error" });
    } finally {
      setRunning("");
      load();
    }
  };

  const openDetail = async (runId) => {
    setDetail({ loadingId: runId });
    setDetailLoading(true);
    try {
      const { data } = await axiosInstance.get(`${URL()}/runs/${runId}`);
      setDetail(data?.data || null);
    } catch (err) {
      setDetail(null);
      setError(err?.response?.data?.message || "Could not load that run");
    } finally {
      setDetailLoading(false);
    }
  };

  const yesterday = moment().subtract(1, "day").format("YYYY-MM-DD");

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {notice.text && (
        <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice({ text: "", severity: "success" })}>
          {notice.text}
        </Alert>
      )}
      {!loading && notSynced > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>{notSynced} day{notSynced === 1 ? "" : "s"}</strong> in this range did not reach the HRMS. Use{" "}
          <strong>Send now</strong> on the day to push it.
        </Alert>
      )}

      <Paper
        sx={{
          p: 2,
          mb: 2,
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
          boxShadow: "none",
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="body2" sx={{ color: "#6b7280" }}>
          Each day's attendance is sent automatically the next day at <strong>{syncTime}</strong>, with retries every
          30 minutes if it fails.
          {startDate && <> Sync covers days from <strong>{moment(startDate).format("D MMM YYYY")}</strong>.</>}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <TextField select size="small" label="Show" value={range} onChange={(e) => setRange(e.target.value)} sx={{ width: 130 }}>
          <MenuItem value={7}>Last 7 days</MenuItem>
          <MenuItem value={14}>Last 14 days</MenuItem>
          <MenuItem value={31}>Last 31 days</MenuItem>
          <MenuItem value={90}>Last 90 days</MenuItem>
        </TextField>
        <TextField
          type="date"
          size="small"
          label="Send a day"
          value={pickDate}
          onChange={(e) => setPickDate(e.target.value)}
          inputProps={{ max: yesterday, min: startDate || undefined }}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="contained"
          startIcon={running === pickDate ? <CircularProgress size={14} color="inherit" /> : <Send size={16} />}
          disabled={
            Boolean(running) || !pickDate || pickDate > yesterday || Boolean(startDate && pickDate < startDate)
          }
          onClick={() => runFor(pickDate)}
          sx={{ bgcolor: "#0C237B", "&:hover": { bgcolor: "#1a3399" }, borderRadius: "8px", textTransform: "none" }}
        >
          Send now
        </Button>
        <Button onClick={load} startIcon={<RefreshCw size={16} />} sx={{ textTransform: "none", color: "#0C237B" }}>
          Refresh
        </Button>
      </Paper>

      <Paper sx={{ borderRadius: "12px", border: "1px solid #e5e7eb", boxShadow: "none", overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700, color: "#6b7280", bgcolor: "#f9fafb" } }}>
                  <TableCell>Attendance Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Sent At</TableCell>
                  <TableCell>Trigger</TableCell>
                  <TableCell align="right">Sent</TableCell>
                  <TableCell align="right">Failed</TableCell>
                  <TableCell align="right">Skipped</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!days.length && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5, color: "#9ca3af" }}>
                      No days to show yet.
                    </TableCell>
                  </TableRow>
                )}
                {days.map((d) => {
                  const meta = STATE_META[d.state] || STATE_META.not_synced;
                  const run = d.latest;
                  const alarming = d.state === "not_synced" || d.state === "failed";
                  return (
                    <TableRow key={d.date} hover sx={{ bgcolor: alarming ? "#fff7f7" : undefined }}>
                      <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                        {moment(d.date).format("ddd, D MMM YYYY")}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={run?.error || ""}>
                          <span>
                            <Pill meta={meta} />
                          </span>
                        </Tooltip>
                        {run?.openShifts > 0 && (
                          <Tooltip title={`${run.openShifts} shift(s) were still open when this ran; their time was not included`}>
                            <Chip label={`${run.openShifts} open`} size="small" sx={{ ml: 0.5, height: 20, fontSize: 10, bgcolor: "#fffbeb", color: "#d97706" }} />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {run?.finishedAt ? (
                          moment(run.finishedAt).format("D MMM, HH:mm")
                        ) : d.state === "scheduled" ? (
                          <Typography variant="body2" sx={{ color: "#6b7280" }}>
                            Sends {moment(d.sendsAt).format("D MMM, HH:mm")}
                          </Typography>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>
                        {run ? (
                          <>
                            {TRIGGER_LABEL[run.trigger] || run.trigger}
                            {run.triggeredByName && (
                              <Typography variant="caption" sx={{ display: "block", color: "#9ca3af" }}>
                                by {run.triggeredByName}
                              </Typography>
                            )}
                          </>
                        ) : (
                          "—"
                        )}
                        {d.runs > 1 && (
                          <Typography variant="caption" sx={{ display: "block", color: "#9ca3af" }}>
                            {d.runs} runs
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ color: "#059669", fontWeight: 600 }}>{run ? run.totals.sent : "—"}</TableCell>
                      <TableCell align="right" sx={{ color: run?.totals.failed ? "#dc2626" : undefined, fontWeight: 600 }}>
                        {run ? run.totals.failed : "—"}
                      </TableCell>
                      <TableCell align="right">{run ? run.totals.skipped : "—"}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        {run && (
                          <Button size="small" startIcon={<Eye size={14} />} onClick={() => openDetail(run.id)} sx={{ textTransform: "none", color: "#374151" }}>
                            Details
                          </Button>
                        )}
                        {d.state !== "scheduled" && d.state !== "running" && (
                          <Button
                            size="small"
                            startIcon={running === d.date ? <CircularProgress size={12} /> : <Send size={14} />}
                            disabled={Boolean(running)}
                            onClick={() => runFor(d.date)}
                            sx={{ textTransform: "none", color: alarming ? "#dc2626" : "#0C237B", fontWeight: alarming ? 700 : 500 }}
                          >
                            {alarming ? "Send now" : "Re-send"}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ── Run detail ─────────────────────────────────────────────────── */}
      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {detail?.date ? `HRMS sync — ${moment(detail.date).format("D MMM YYYY")}` : "HRMS sync"}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading || !detail?.rows ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="body2" sx={{ color: "#6b7280", mb: 1.5 }}>
                {TRIGGER_LABEL[detail.trigger]} · started {moment(detail.startedAt).format("D MMM, HH:mm:ss")}
                {detail.finishedAt && ` · finished ${moment(detail.finishedAt).format("HH:mm:ss")}`}
                {detail.error && ` · ${detail.error}`}
              </Typography>
              {!detail.rows.length ? (
                <Typography sx={{ color: "#9ca3af", py: 2 }}>No agents had attendance on this day.</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ "& th": { fontWeight: 700, color: "#6b7280" } }}>
                      <TableCell>Agent</TableCell>
                      <TableCell>Emp ID</TableCell>
                      <TableCell>Time Sent</TableCell>
                      <TableCell>Status Sent</TableCell>
                      <TableCell>Result</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.rows.map((r) => (
                      <TableRow key={String(r.agent)}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.agentName}</Typography>
                          <Typography variant="caption" sx={{ color: "#9ca3af" }}>{r.agentUserId}</Typography>
                        </TableCell>
                        <TableCell>{r.empid || "—"}</TableCell>
                        <TableCell>{r.payload?.attend_login || "—"}</TableCell>
                        <TableCell>
                          {r.payload?.attend_type || "—"}
                          {r.corrected && (
                            <Chip label="corrected" size="small" sx={{ ml: 0.5, height: 18, fontSize: 10, bgcolor: "#eef2ff", color: "#0C237B" }} />
                          )}
                        </TableCell>
                        <TableCell>
                          <Pill meta={ROW_META[r.status] || ROW_META.skipped} />
                          {r.error && (
                            <Typography variant="caption" sx={{ display: "block", color: "#6b7280", maxWidth: 260 }}>
                              {r.error}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
